import * as React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import Col from 'antd/lib/grid/col';
import Row from 'antd/lib/grid/row';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
    selectAllIntegrationsForCapabilities,
    selectAppleAppStoreData,
    selectFeatureManagementIntegration,
    selectIsReleaseKickedOff,
    selectReleaseOverviewStats,
    selectReleaseVersion,
} from '../../selectors/selectors';
import {
    AppleAppStoreState,
    IntegrationCapability,
    OverviewSection,
    Statistic,
    StatisticType,
    StatisticWithAssociatedInfo,
} from '../../types';
import { statisticsForOverviewSection } from '../../helpers';
import SectionHeader from './SectionHeader';
import { ReleaseOverviewStatCard } from '../StatCard';
import { antGrey } from '../styles/globalStyles';
import ContributorChart from './ContributorChart';
import { GRID_GAP, GRID_CARD_MIN_WIDTH, GRID_MIN_WIDTH } from '../../constants';
import { displayNameForIntegrationType } from '../../helpers/integrationHelpers';
import { FeatureSection, Nullable } from '../../typescriptHelpers';
import { EmptyStateWrapper } from '../releaseEmptyState/EmptyStateWrapper';

interface ProductSprintSectionProps {
    appId: string;
    releaseId: string;
    isLoadingReleaseStatsHistory: boolean;
    releaseStatsByTypes: Partial<Record<StatisticType, StatisticWithAssociatedInfo[]>>;
    currentReleaseStats: Statistic[];
    storeRejectedOn: Nullable<string>;
    devRejectedOn: Nullable<string>;
}

const ProductSprintSection: React.FC<ProductSprintSectionProps> = (props: ProductSprintSectionProps) => {
    const {
        appId,
        releaseId,
        isLoadingReleaseStatsHistory,
        releaseStatsByTypes,
        currentReleaseStats,
        storeRejectedOn,
        devRejectedOn,
    } = props;
    const currentReleaseVersion = useSelector((state: RootState) => selectReleaseVersion(state, releaseId));

    const statistics = currentReleaseStats ?? [];
    const contributingTeams = useSelector(
        (state: RootState) => selectReleaseOverviewStats(state, releaseId)?.contributingTeams,
    );

    const isKickedOff = useSelector((state: RootState) => selectIsReleaseKickedOff(state, releaseId));
    const integrations = useSelector((state: RootState) =>
        selectAllIntegrationsForCapabilities(state, appId, [
            IntegrationCapability.BuildSystem,
            IntegrationCapability.AppStore,
            IntegrationCapability.FeatureManagement,
        ]),
    );

    const isDeveloperRejected = useSelector(
        (state: RootState) =>
            selectAppleAppStoreData(state, releaseId)?.versionReviewSubmissionItem?.state ===
            AppleAppStoreState.DeveloperRejected,
    );
    const isStoreRejected = useSelector(
        (state: RootState) =>
            selectAppleAppStoreData(state, releaseId)?.versionReviewSubmissionItem?.state ===
            AppleAppStoreState.Rejected,
    );

    const wasDeveloperRejected = !!storeRejectedOn;
    const wasStoreRejected = !!devRejectedOn;

    const featureManagementIntegration = useSelector((state: RootState) =>
        selectFeatureManagementIntegration(state, appId),
    );
    const integrationDisplayName = displayNameForIntegrationType(featureManagementIntegration?.id);

    if (!statistics) {
        return null;
    }
    const emptyChange = <Text style={{ color: antGrey.grey7, fontSize: '18px' }}>--</Text>;

    const productSprintStats = statistics.filter((stat) => {
        return statisticsForOverviewSection(OverviewSection.ProductSprint).includes(stat.type);
    });

    return (
        <div>
            <SectionHeader
                header={
                    <Space>
                        <span>Product sprint</span>
                        <EmptyStateWrapper
                            appId={appId}
                            placement="right"
                            shouldHideDotOnHover={false}
                            featureSections={[FeatureSection.OverviewProduct]}
                            releaseId={releaseId}
                            shouldShowAsTooltip
                            customWrapperStyle={{ height: 20 }}
                        />
                    </Space>
                }
                relatedIntegrations={integrations}
            />

            <Row>
                <Col span={20}>
                    <div
                        style={{
                            display: 'grid',
                            gap: GRID_GAP,
                            gridTemplateColumns: `repeat(auto-fill, minmax(${GRID_CARD_MIN_WIDTH}px, 1fr))`,
                            minWidth: GRID_MIN_WIDTH,
                            alignItems: 'start',
                        }}
                    >
                        {productSprintStats.map((stat) => {
                            const sampleStats = releaseStatsByTypes[stat.type];

                            return (
                                <ReleaseOverviewStatCard
                                    key={stat.type}
                                    type={stat.type}
                                    change={isKickedOff ? stat.change : emptyChange}
                                    changeStatus={isKickedOff ? stat.changeStatus : null}
                                    value={stat.value}
                                    unit={stat.unit}
                                    average={stat.average}
                                    isDeveloperRejected={isDeveloperRejected}
                                    isStoreRejected={isStoreRejected}
                                    isLoadingStats={isLoadingReleaseStatsHistory}
                                    highlightedVersion={currentReleaseVersion}
                                    sampleStats={sampleStats}
                                    wasRejected={wasDeveloperRejected || wasStoreRejected}
                                />
                            );
                        })}
                        {contributingTeams.length > 0 && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '175px',
                                    borderRadius: '8px',
                                    backgroundColor: antGrey.grey2,
                                    color: antGrey.grey7,
                                    padding: '18px',
                                }}
                            >
                                <ContributorChart
                                    title={
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <Text style={{ color: antGrey.grey7 }}>Teams contributing</Text>
                                            <Tooltip
                                                title={`Based on the number of items of work associated with a team in ${
                                                    integrationDisplayName || `your project management software`
                                                }`}
                                            >
                                                <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                                            </Tooltip>
                                        </div>
                                    }
                                    members={contributingTeams}
                                />
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default ProductSprintSection;
